import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useAlive} from '@github-ui/use-alive'
import {BellIcon, BellSlashIcon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState, useTransition} from 'react'
import {graphql, useRefetchableFragment, useRelayEnvironment} from 'react-relay'

import {ALIVE_REASONS} from '../../../constants/alive'
import {LABELS} from '../../../constants/labels'
import {commitUpdateIssueSubscriptionMutation} from '../../../mutations/update-issue-subscription'
import type {IssueViewerViewer$data} from '../../__generated__/IssueViewerViewer.graphql'
import type {IssueSecondaryNode} from '../../issue-viewer-secondary-query-types'
import type {SubscriptionSectionRefetchableFragment$key} from './__generated__/SubscriptionSectionRefetchableFragment.graphql'
import {NotificationSettingsDialog} from './NotificationSettingsDialog'
import styles from './SubscriptionSection.module.css'

// Plain fields derived from the allowlisted `IssueViewerSecondaryViewQuery`, intersected with the
// refetchable `$key` so the still-Relay child can read the same `issue` ref.
// RELAY-CLEANUP(SubscriptionSectionRefetchableFragment): remove the `RelayCompat` + `$key` intersection
// once the refetchable fragment leaves Relay.
type SubscriptionSectionFragment = RelayCompat<Pick<IssueSecondaryNode, 'id' | 'threadSubscriptionChannel'>> &
  SubscriptionSectionRefetchableFragment$key

export type SubscriptionSectionProps = {
  issue: SubscriptionSectionFragment
  viewer: IssueViewerViewer$data | null
}

export function SubscriptionSection({issue, viewer}: SubscriptionSectionProps) {
  const {threadSubscriptionChannel, id} = issue

  // threadSubscriptionChannel should not be null for logged-in users, but check to ensure that it is loaded
  // because useAlive expects a valid channel name.
  return threadSubscriptionChannel ? (
    <SubscriptionSectionInternal
      issue={issue}
      id={id}
      threadSubscriptionChannel={threadSubscriptionChannel}
      viewer={viewer}
    />
  ) : null
}

export function SubscriptionSectionInternal({
  id,
  issue,
  threadSubscriptionChannel,
  viewer,
}: {
  id: string
  issue: SubscriptionSectionRefetchableFragment$key
  threadSubscriptionChannel: string
  viewer: IssueViewerViewer$data | null
}) {
  const shouldUpdateAnnouncement = useRef(false)
  const environment = useRelayEnvironment()
  const [, startTransition] = useTransition()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const announceSubscriptionUpdates = useCallback((newSubscribed: boolean, success = true) => {
    if (success) {
      const message = newSubscribed
        ? LABELS.notifications.subscribedAnnouncement
        : LABELS.notifications.unsubscribedAnnouncement
      announce(message)
    } else {
      announce(LABELS.somethingWentWrong)
    }
  }, [])

  const [data, refetch] = useRefetchableFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SubscriptionSectionRefetchableFragment on Issue
      @refetchable(queryName: "SubscriptionSectionRefetchableFragmentQuery") {
        id
        viewerThreadSubscriptionFormAction
        viewerCustomSubscriptionEvents
      }
    `,
    issue,
  )
  const {viewerThreadSubscriptionFormAction, viewerCustomSubscriptionEvents, id: issueId} = data
  const subscribed = viewerThreadSubscriptionFormAction === 'UNSUBSCRIBE'
  // Spreading this because it's 'readonly' and cannot be assigned to the mutable type 'ThreadSubscriptionEvent[]' when passing it to the Dialog
  const preSelectedCustomOptions = viewerCustomSubscriptionEvents ? [...viewerCustomSubscriptionEvents] : []

  const refreshSubscription = useCallback(() => {
    const query = () => {
      refetch(
        {},
        {
          fetchPolicy: 'network-only',
          onComplete: () => {
            shouldUpdateAnnouncement.current = true
          },
        },
      )
    }
    startTransition(() => query())
  }, [refetch, startTransition])

  useEffect(() => {
    if (shouldUpdateAnnouncement.current) {
      announceSubscriptionUpdates(subscribed)
      shouldUpdateAnnouncement.current = false
    }
  }, [subscribed, announceSubscriptionUpdates])

  const toggleSubscription = useCallback(() => {
    const newSubscribed = !subscribed
    const newSubscriptionState = newSubscribed ? 'SUBSCRIBED' : 'UNSUBSCRIBED'
    sendEvent('notifications.thread_subscription.toggle', {
      subscriptionState: newSubscriptionState,
      threadClass: 'Issue',
      threadId: id,
    })

    commitUpdateIssueSubscriptionMutation({
      environment,
      input: {
        subscribableId: id,
        state: newSubscriptionState,
      },
      onCompleted: () => {
        announceSubscriptionUpdates(newSubscribed)
      },
      onError: () => announceSubscriptionUpdates(subscribed, false),
    })
  }, [environment, id, subscribed, announceSubscriptionUpdates])

  // Thread subscription updates are scoped to an individual user and emitted via signed user channels.
  useAlive(threadSubscriptionChannel, (event: {reason: string}) => {
    if (!event?.reason) return
    // Refetch subscription information if we receive a message that the subscription has changed.
    // We cannot rely on the "reason" to determine the new state, because we still receive a subscription
    // message if the thread is "ignored".
    if (event.reason === ALIVE_REASONS.SUBSCRIBED && !subscribed) {
      refreshSubscription()
    } else if (event.reason === ALIVE_REASONS.UNSUBSCRIBED && subscribed) {
      refreshSubscription()
    }
  })

  const buttonRef = useRef<HTMLButtonElement>(null)

  const sectionHeader = (
    <div className={clsx(styles.container)}>
      <Heading id={id} as="h3" className={clsx(styles.header)}>
        Notifications
      </Heading>
      <button onClick={() => setIsDialogOpen(true)} className={clsx(styles.button)} ref={buttonRef}>
        {LABELS.notifications.customizeNotificationsSettings}
      </button>
      {isDialogOpen && (
        <NotificationSettingsDialog
          title={LABELS.notifications.notificationsSettings}
          onClose={() => setIsDialogOpen(false)}
          returnFocusRef={buttonRef}
          threadId={issueId}
          preselectedViewerSubscriptionEvents={preSelectedCustomOptions}
          subscribed={subscribed}
        />
      )}
    </div>
  )

  // It controls whether email and web notifications for issues should be sent
  // via notifyd, and whether notifyd should be the primary source of truth
  // for issues related subscriptions and settings.
  const isNotifydEnabledIssuesWatchActivity = isFeatureEnabled('notifyd_issue_watch_activity_notify')

  // Controls whether the settings for sending notifications for
  // participating activity should be written to notify
  const isNotifydEnabledIssuesThreadSubscription = isFeatureEnabled('notifyd_enable_issue_thread_subscriptions')

  // Both of the above flags are used in the backend logic of when to show the customise option: https://github.com/github/github/blob/66d24898297b3a2eba7133165ae6c2ab72c3f64a/packages/issues/app/models/issue/notifyd_adapter.rb#L26%7CIssue::NotifydAdapter
  // If they are both enabled, it means that the customisation option should not be shown
  const customizeDisabled =
    viewer?.isEnterpriseManagedUser || isNotifydEnabledIssuesThreadSubscription || isNotifydEnabledIssuesWatchActivity

  return (
    <Section
      sectionHeader={
        customizeDisabled ? <ReadonlySectionHeader title={LABELS.notifications.notificationsTitle} /> : sectionHeader
      }
    >
      <div className="width-full d-flex flex-column flex-items-stretch pt-2 px-2">
        <Button
          aria-describedby="issue-viewer-subscription-description"
          leadingVisual={subscribed ? BellSlashIcon : BellIcon}
          onClick={toggleSubscription}
          size="small"
        >
          {subscribed ? LABELS.notifications.subscribedButton : LABELS.notifications.unsubscribedButton}
        </Button>
        <p id="issue-viewer-subscription-description" className="my-2 f6 fgColor-muted">
          {subscribed ? LABELS.notifications.subscribedDescription : LABELS.notifications.unsubscribedDescription}
        </p>
      </div>
    </Section>
  )
}

export function SubscriptionSectionFallback() {
  return (
    <Section sectionHeader={<ReadonlySectionHeader title="Notifications" />}>
      <div className="width-full d-flex flex-column flex-items-stretch pt-2 px-2">
        <Button aria-describedby="issue-viewer-subscription-description" leadingVisual={BellIcon}>
          {LABELS.notifications.unsubscribedButton}
        </Button>
        <p id="issue-viewer-subscription-description" className="my-2 f6 fgColor-muted">
          {LABELS.notifications.unsubscribedDescription}
        </p>
      </div>
    </Section>
  )
}
